import React from "react";
import {
  EuiBadge,
  EuiBasicTableColumn,
  EuiFilterButton,
  EuiFilterGroup,
  EuiFlexGroup,
  EuiFlexItem,
  EuiIcon,
  EuiInMemoryTable,
  EuiLink,
  EuiText,
  EuiToolTip,
} from "@elastic/eui";
import { isOplItemClosed, OplItem, OplItemPriority, OplItemType } from "src/questionnaires";
import { fmtDate, lastLogEntry, ownerShort } from "./format";

/**
 * The list of OPEN OPL points, most urgent first - shared by the appliance
 * flyout's OPL tab (one appliance) and the cross-appliance overview page.
 */

const DAY = 24 * 60 * 60 * 1000;

export type DueFilter = "all" | "overdue" | "week" | "none";

export interface OpenPointRow extends OplItem {
  /** Whole days until due (negative = overdue); null without a due date. */
  dueInDays: number | null;
  /** Sort key: overdue first, then by date, undated last. */
  dueSort: number;
  lastLog: { date: string; text: string } | null;
}

export type OplT = (k: string, d: string, values?: Record<string, unknown>) => string;

const PRIORITY_COLOR: Record<OplItemPriority, string> = {
  high: "danger",
  medium: "default",
  low: "hollow",
};

const PRIORITY_RANK: Record<OplItemPriority, number> = { high: 0, medium: 1, low: 2 };

/** Open items only, with due-date facts precomputed for sorting/filtering. */
export const toOpenPointRows = (items: OplItem[]): OpenPointRow[] => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return items
    .filter((item) => !isOplItemClosed(item.status))
    .map((item) => {
      let dueInDays: number | null = null;
      if (item.dueDate) {
        const d = new Date(item.dueDate);
        d.setHours(0, 0, 0, 0);
        dueInDays = Math.round((d.getTime() - today.getTime()) / DAY);
      }
      return {
        ...item,
        dueInDays,
        dueSort: dueInDays === null ? Number.MAX_SAFE_INTEGER : dueInDays,
        lastLog: lastLogEntry(item),
      };
    });
};

export const matchesDue = (row: OpenPointRow, due: DueFilter): boolean => {
  const d = row.dueInDays;
  switch (due) {
    case "overdue":
      return d !== null && d < 0;
    case "week":
      return d !== null && d >= 0 && d <= 7;
    case "none":
      return d === null;
    default:
      return true;
  }
};

export const dueCountsOf = (rows: OpenPointRow[]): Record<DueFilter, number> => ({
  all: rows.length,
  overdue: rows.filter((r) => matchesDue(r, "overdue")).length,
  week: rows.filter((r) => matchesDue(r, "week")).length,
  none: rows.filter((r) => matchesDue(r, "none")).length,
});

/** The Alle / Überfällig / In 7 Tagen / Ohne Termin chips, with counts. */
export const DueFilterChips = ({
  t,
  value,
  counts,
  onChange,
}: {
  t: OplT;
  value: DueFilter;
  counts: Record<DueFilter, number>;
  onChange: (next: DueFilter) => void;
}): JSX.Element => {
  const chips: Array<{ id: DueFilter; label: string }> = [
    { id: "all", label: t("overview.filter.all", "All") },
    { id: "overdue", label: t("overview.filter.overdue", "Overdue") },
    { id: "week", label: t("overview.filter.week", "Due within 7 days") },
    { id: "none", label: t("overview.filter.none", "No due date") },
  ];
  return (
    <EuiFilterGroup aria-label={t("overview.filter.aria", "Filter by due date")}>
      {chips.map((chip) => (
        <EuiFilterButton
          key={chip.id}
          hasActiveFilters={value === chip.id}
          isSelected={value === chip.id}
          numFilters={counts[chip.id]}
          onClick={() => onChange(value === chip.id && chip.id !== "all" ? "all" : chip.id)}
        >
          {chip.label}
        </EuiFilterButton>
      ))}
    </EuiFilterGroup>
  );
};

const dueCell = (t: OplT, row: OpenPointRow) => {
  if (row.dueInDays === null) {
    return (
      <EuiText size="xs" color="subdued">
        {t("overview.due.none", "no due date")}
      </EuiText>
    );
  }
  const date = fmtDate(row.dueDate);
  if (row.dueInDays < 0) {
    return (
      <EuiFlexGroup gutterSize="xs" alignItems="center" responsive={false}>
        <EuiFlexItem grow={false}>
          <EuiIcon type="alert" color="danger" size="s" />
        </EuiFlexItem>
        <EuiFlexItem grow={false}>
          <EuiText size="s" color="danger">
            <strong>{date}</strong>
          </EuiText>
          <EuiText size="xs" color="danger">
            {t("overview.due.overdue", "{{days}} days overdue", {
              days: Math.abs(row.dueInDays),
            })}
          </EuiText>
        </EuiFlexItem>
      </EuiFlexGroup>
    );
  }
  return (
    <div>
      <EuiText size="s" color={row.dueInDays <= 7 ? "warning" : "default"}>
        <strong>{date}</strong>
      </EuiText>
      <EuiText size="xs" color="subdued">
        {row.dueInDays === 0
          ? t("overview.due.today", "due today")
          : t("overview.due.inDays", "in {{days}} days", { days: row.dueInDays })}
      </EuiText>
    </div>
  );
};

export const OpenPointsTable = ({
  t,
  rows,
  loading = false,
  onOpen,
  onOpenAppliance,
  showAppliance = true,
  pageSize = 25,
  emptyMessage,
}: {
  t: OplT;
  rows: OpenPointRow[];
  loading?: boolean;
  /** Open the point on its appliance's board. */
  onOpen: (row: OpenPointRow) => void;
  onOpenAppliance?: (applianceName: string) => void;
  /** Off in the flyout, where every row is the same appliance. */
  showAppliance?: boolean;
  pageSize?: number;
  emptyMessage: string;
}): JSX.Element => {
  const columns: Array<EuiBasicTableColumn<OpenPointRow>> = [
    {
      field: "dueSort",
      name: t("overview.column.due", "Due"),
      sortable: true,
      width: "130px",
      render: (_: number, row: OpenPointRow) => dueCell(t, row),
    },
    ...(showAppliance
      ? [
          {
            field: "applianceName",
            name: t("overview.column.appliance", "Appliance"),
            sortable: true,
            width: "190px",
            render: (name: string) =>
              onOpenAppliance ? (
                <EuiLink onClick={() => onOpenAppliance(name)}>{name}</EuiLink>
              ) : (
                name
              ),
          } as EuiBasicTableColumn<OpenPointRow>,
        ]
      : []),
    {
      field: "title",
      name: t("overview.column.point", "Point"),
      sortable: true,
      render: (title: string, row: OpenPointRow) => (
        <div style={{ minWidth: 0 }}>
          <EuiText size="s">
            <EuiLink onClick={() => onOpen(row)}>
              <span style={{ opacity: 0.7 }}>{row.key}</span> <strong>{title}</strong>
            </EuiLink>
          </EuiText>
          {row.lastLog && (
            <EuiText
              size="xs"
              color="subdued"
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: 480,
              }}
              title={row.lastLog.text}
            >
              {row.lastLog.date}: {row.lastLog.text}
            </EuiText>
          )}
        </div>
      ),
    },
    {
      field: "type",
      name: t("overview.column.type", "Type"),
      sortable: true,
      width: "115px",
      render: (value: OplItemType) => (
        <EuiBadge color="hollow">{t(`type.${value}`, value)}</EuiBadge>
      ),
    },
    {
      field: "priority",
      name: t("overview.column.priority", "Priority"),
      sortable: (row: OpenPointRow) => PRIORITY_RANK[row.priority],
      width: "95px",
      render: (value: OplItemPriority) => (
        <EuiBadge color={PRIORITY_COLOR[value]}>{t(`priority.${value}`, value)}</EuiBadge>
      ),
    },
    {
      field: "owner",
      name: t("overview.column.owner", "Owner"),
      sortable: true,
      width: "130px",
      render: (value: string | null, row: OpenPointRow) => (
        <EuiFlexGroup gutterSize="xs" alignItems="center" responsive={false}>
          <EuiFlexItem grow={false}>
            <EuiToolTip content={value ?? undefined}>
              <EuiText size="s">{ownerShort(value)}</EuiText>
            </EuiToolTip>
          </EuiFlexItem>
          {row.assignedInAbsence && (
            <EuiFlexItem grow={false}>
              <EuiToolTip content={t("overview.assignedInAbsence", "Assigned while absent")}>
                <EuiIcon type="user" color="warning" size="s" />
              </EuiToolTip>
            </EuiFlexItem>
          )}
        </EuiFlexGroup>
      ),
    },
    {
      name: t("overview.column.since", "Since"),
      width: "100px",
      render: (row: OpenPointRow) => (
        <EuiText size="xs" color="subdued">
          {row.openedIn?.key ?? fmtDate(row.createdAt)}
        </EuiText>
      ),
    },
    {
      name: t("overview.column.refs", "Linked"),
      width: "130px",
      render: (row: OpenPointRow) => (
        <EuiFlexGroup gutterSize="xs" responsive={false} wrap>
          {row.gitlabIssueUrl && (
            <EuiFlexItem grow={false}>
              <EuiBadge
                color="hollow"
                href={row.gitlabIssueUrl}
                target="_blank"
                aria-label={t("overview.openGitlab", "Open GitLab issue")}
              >
                GitLab
              </EuiBadge>
            </EuiFlexItem>
          )}
          {row.otoboTicketUrl && (
            <EuiFlexItem grow={false}>
              <EuiBadge
                color="hollow"
                href={row.otoboTicketUrl}
                target="_blank"
                aria-label={t("overview.openOtobo", "Open OTOBO ticket {{number}}", {
                  number: row.otoboTicketNumber ?? "",
                })}
              >
                OTOBO{row.otoboTicketNumber ? ` #${row.otoboTicketNumber}` : ""}
              </EuiBadge>
            </EuiFlexItem>
          )}
        </EuiFlexGroup>
      ),
    },
  ];

  return (
    <EuiInMemoryTable<OpenPointRow>
      items={rows}
      columns={columns}
      loading={loading}
      rowProps={(row: OpenPointRow) => ({
        style: { cursor: "pointer" },
        onClick: (e: React.MouseEvent) => {
          if ((e.target as HTMLElement).closest("a, button")) return;
          onOpen(row);
        },
      })}
      sorting={{ sort: { field: "dueSort", direction: "asc" } }}
      pagination={{ initialPageSize: pageSize, pageSizeOptions: [10, 25, 50, 100] }}
      tableLayout="auto"
      message={emptyMessage}
    />
  );
};
